import { all, takeLeading, takeLatest, put, call, select, takeEvery } from 'redux-saga/effects'
import { history } from 'index'
import { getConfig, setEnvParam, getEnvParam, getTempEnvParam, removeTempEnvParam } from 'env'
import { getSearchParams, DEFAULT_AUTH_PAGE } from 'utils'
import conf, { isGlobal } from 'conf'
import { getSettings } from 'services/landing'
import { getPaidLicense } from 'services/landing/utils/api'
import ResourceService from 'api/Resource'
import AuthService from 'api/Auth'
import { getUMKFromLocal, deleteUMKFromLocal } from 'pUtils'

import store from 'store'

import * as landing from 'services/landing'
import * as landingAccount from 'services/landing/account'
import * as landingBasic from 'services/landing/basic'

import { clearAttachmentCache } from 'utils/attachmentCache'
import { clearDeviceKeys } from 'services/passkey/deviceUnlock'
import { notifyExtensionMasterPassword } from 'utils/extensionBridge'
import actions from './actions.ts'

const mapAuthProviders = {
  landing: {
    loginByToken: landing.loginByToken,
    umkLogin: landing.umkLogin,
    umkRegister: landing.umkRegister,
    umkChangePW: landing.umkChangePW,
    currentAccount: landingAccount.currentAccount,
    logout: landing.logout,
    basicRegister: landingBasic.basicRegister,
    basicLogin: landingBasic.basicLogin,
    currentBasicAccount: landingAccount.currentBasicAccount,
    basicInviteInfo: landingBasic.basicInviteInfo,
  },
}

export function* BASIC_LOGIN({ payload }) {
  const { email, password, saveToken, onComplete } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  try {
    const { authProvider: autProviderName } = yield select((state) => state.settings)
    const success = yield call(
      mapAuthProviders[autProviderName].basicLogin,
      email,
      password,
      saveToken,
    )

    if (success) {
      yield INNER_LOAD_CURRENT_ACCOUNT(true)
    }

    yield put({
      type: 'user/SET_STATE',
      payload: {
        loading: false,
      },
    })

    yield put({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'showZeroDarkWeb',
        value: true,
      },
    })
  } finally {
    if (typeof onComplete === 'function') {
      onComplete()
    }
  }
}

export function* SET_RECOVERY_UMK({ payload }) {
  const { recoveryUMK } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: { recoveryUMK },
  })

  yield INNER_LOAD_CURRENT_ACCOUNT(true)
}

export function* SET_RESET_PW({ payload }) {
  const { resetPW } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: { resetPW },
  })
}

export function* BASIC_REGISTER({ payload }) {
  const { request } = payload

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })

  const { authProvider: autProviderName } = yield select((state) => state.settings)
  yield call(mapAuthProviders[autProviderName].basicRegister, request)

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })

  history.push('/masterPW/register')
}

export function* LOGIN_BY_TOKEN({ payload }) {
  const { tokenInfo } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  const { authProvider: autProviderName } = yield select((state) => state.settings)
  const result = yield call(mapAuthProviders[autProviderName].loginByToken, tokenInfo)
  if (result === 'success') {
    yield INNER_LOAD_CURRENT_ACCOUNT(true)
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })

  if (result !== 'success') {
    yield history.push(DEFAULT_AUTH_PAGE)
  }
}

export function* SET_WORKSPACE_NAME({ payload }) {
  const { workspaceName } = payload

  const { workspace } = yield select((state) => state.user)
  const { workspaceList } = yield select((state) => state.user)
  const { id: workspaceId } = workspace

  const updatedWorkspaceList = workspaceList.map((value) =>
    value.id === Number(workspaceId) ? { ...value, name: workspaceName } : value,
  )

  yield put({
    type: 'user/SET_STATE',
    payload: {
      workspaceList: updatedWorkspaceList,
      workspace: { id: workspaceId, workspace_name: workspaceName },
    },
  })
}

export function* SET_USER_NAME({ payload }) {
  const { name } = payload

  const safebox = yield select((state) => state.user?.safebox || {})

  yield put({
    type: 'user/SET_STATE',
    payload: {
      name,
      safebox: {
        ...safebox,
        name,
      },
    },
  })
}

export function* SET_USER_AVATAR({ payload }) {
  const { avatar } = payload

  yield put({
    type: 'user/SET_STATE',
    payload: {
      avatar,
    },
  })
}

export function* SELECT_WORKSPACE({ payload }) {
  const { workspace, role, id } = payload
  yield store.set('app.user.workspace_id', workspace.id)
  const { safebox } = yield select((state) => state.user || {})

  const newSafebox = { ...safebox, userId: id, role }

  yield put({
    type: 'user/SET_STATE',
    payload: { workspace, role, id, safebox: newSafebox },
  })

  yield call(setEnvParam, 'appType', JSON.stringify(newSafebox))

  const license = yield call(getPaidLicense)
  if (license?.code === 200) {
    yield put({
      type: 'user/SET_STATE',
      payload: { paidLicense: license.data },
    })
  }

  const { leaked } = getSearchParams()
  if (leaked === 'true') {
    yield put({
      type: 'settings/SET_IS_LEAKED',
      payload: { isLeaked: false },
    })
  }

  yield call(getSettings)
}

export function* UMK_LOGIN({ payload }) {
  const { password, path, preloadedLoginData } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: true,
    },
  })
  const { authProvider } = yield select((state) => state.settings)
  // preloadedLoginData: Passkey 경로에서 /v1/auth/passkey/login 응답 재사용 — 중복 /v1/auth/login 호출 skip.
  const success = yield call(mapAuthProviders[authProvider].umkLogin, password, preloadedLoginData)

  if (success) {
    console.log(' --- umk login success --- ')
    // 검증된 마스터 비밀번호를 익스텐션으로 동기화 (로그인 비번 ≠ 마스터 비번).
    notifyExtensionMasterPassword(password)

    let nextPath = path
    if (
      nextPath == null ||
      nextPath.indexOf('/masterPW/') === 0 ||
      nextPath.indexOf('/expired') === 0
    ) {
      nextPath = getConfig('defaultPage') || '/'
    }

    // window.dispatchEvent(new CustomEvent('darkWebFetchData'))
    yield history.push(nextPath)
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: false,
    },
  })
}

export function* UMK_REGISTER({ payload }) {
  const { password } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: true,
    },
  })

  const { authProvider } = yield select((state) => state.settings)
  const success = yield call(mapAuthProviders[authProvider].umkRegister, password)

  if (success) {
    notifyExtensionMasterPassword(password)
    yield INNER_LOAD_CURRENT_ACCOUNT(true)

    history.push(getConfig('defaultPage'))
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: false,
    },
  })
}

export function* UMK_CHANGEPW({ payload }) {
  // PR3 §7.3: wasTemp(임시계정/복구후 강제변경)는 진입점이 명시 전달. 기본 false(자발적 변경).
  // ESCROW 사용자의 was_temp 변경에서만 recovery 키페어 회전 — voluntary 오판 시 복구 붕괴 방지.
  // fromSettings: Settings 모달의 자발적 변경 진입점. 성공 시 reload/navigation 을 skip 하고
  // onComplete(success) 콜백으로 모달에 결과를 알린다(성공→닫기, 실패→제출상태 해제). 미전달 시
  // 기존 동작(임시계정/복구 후 강제변경) 유지. (콜백 패턴: 동일 파일 LOGIN saga 의 onComplete)
  const {
    password,
    newPassword,
    path,
    reloadAccount,
    wasTemp = false,
    fromSettings = false,
    onComplete,
  } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: true,
    },
  })

  // try-finally: umkChangePW/후속 로직에서 예외가 나도 umkProcessing 해제 + onComplete 를
  // 반드시 호출 — 미호출 시 모달 isSubmitting 고착(버튼 영구 비활성). (BASIC_LOGIN 패턴)
  let success = false
  try {
    const { authProvider } = yield select((state) => state.settings)
    success = yield call(
      mapAuthProviders[authProvider].umkChangePW,
      password,
      newPassword,
      wasTemp,
      fromSettings,
    )

    if (success) {
      notifyExtensionMasterPassword(newPassword)

      // #1329 ②: 서버는 revoke-on-key-change 로 이 사용자의 디바이스 키를 이미 폐기했으므로,
      // 로컬 IndexedDB 키가 잔존하면 Settings '이 브라우저에서 사용 중' 이 stale 표시된다.
      // 정리 실패는 비차단 — 잠금 화면 진입 시 841 자가치유 경로가 존재.
      try {
        yield call(clearDeviceKeys)
      } catch (e) {
        console.warn('[DBE] post-changePW local device cleanup failed:', e)
      }

      // Settings 진입은 overlay 모달 컨텍스트라 nav/reload 가 부적절 — skip 하고 콜백만.
      if (!fromSettings) {
        if (reloadAccount) {
          yield INNER_LOAD_CURRENT_ACCOUNT(true)
        } else {
          let nextPath = path
          if (nextPath == null || nextPath.indexOf('/masterPW/') === 0) {
            nextPath = getConfig('defaultPage') || '/'
          }
          yield history.push(nextPath)
        }
      }
    }
  } finally {
    yield put({
      type: 'user/SET_STATE',
      payload: {
        umkProcessing: false,
      },
    })

    // 성공/실패 모두 호출 — 실패 시 모달의 isSubmitting 고착(버튼 영구 비활성) 방지.
    // 에러 팝업은 umkChangePW 가 errorBase 로 이미 표시하므로 여기선 결과 전달만 한다.
    if (fromSettings && typeof onComplete === 'function') {
      onComplete(success)
    }
  }
}

export function* SET_LEAKED_RECORD_COUNT({ payload = {} }) {
  const { leakedCount, callback = true } = payload || {}
  let count = leakedCount != null ? leakedCount : null

  try {
    const { showZeroDarkWeb } = yield select((state) => state.settings)
    if (showZeroDarkWeb === true) {
      if (leakedCount == null && callback === true) {
        const response = yield call(ResourceService.getLeaked)
        // console.log(' -- showZeroDarkWeb : ', showZeroDarkWeb)
        count = response.total
        // return response.total
      }

      yield put({
        type: 'user/SET_STATE',
        payload: { leakedCount: count },
      })

      if (count === 0) {
        yield put({
          type: 'settings/SET_IS_LEAKED',
          payload: { isLeaked: false },
        })
      }

      return count
    }

    return null
  } catch (error) {
    console.error('❌ Error fetching leaked data:', error)
    return null
  }
}

function* redirectWithPathQuery(
  curPath,
  {
    targetPath,
    defaultAddPath = false,
    skipPathPrefixes = [],
    alwaysAddPathPrefixes = [],
    alwaysSamePath = false,
  },
) {
  if (alwaysSamePath) {
    yield history.push(targetPath)
    return
  }

  const shouldAlwaysAddPath = alwaysAddPathPrefixes.some((prefix) => curPath.indexOf(prefix) === 0)
  const shouldSkipPath = skipPathPrefixes.some((prefix) => curPath.indexOf(prefix) === 0)

  if (shouldAlwaysAddPath) {
    yield history.push(`${targetPath}?path=${curPath}`)
  } else if (shouldSkipPath) {
    yield history.push(targetPath)
  } else if (defaultAddPath) {
    yield history.push(`${targetPath}?path=${curPath}`)
  } else {
    yield history.push(targetPath)
  }
}

function* INNER_LOAD_CURRENT_ACCOUNT(useByLogin) {
  try {
    const { authProvider } = yield select((state) => state.settings)
    const resetPW = yield select((state) => state.user.resetPW)
    const { pathname } = window.location
    const { search } = window.location
    const token = new URLSearchParams(search).get('token')

    // viewer, privacy 엔드포인트는 계정 로딩 및 마스터 비밀번호 redirect 건너뛰기
    if (pathname.startsWith('/viewer') || pathname.startsWith('/privacy')) {
      return
    }

    if (pathname.startsWith('/invite/accept') && token != null) {
      const res = yield call(mapAuthProviders[authProvider].basicInviteInfo, token)

      if (store.get('app.user.token') != null) {
        yield put({ type: 'user/LOGOUT' })
      }

      // SAML 초대: LOGOUT 전에 먼저 IdP로 리다이렉트 (중간 화면 전환 방지)
      if (
        res?.code === 200 &&
        res.data?.auth_type === 'saml' &&
        res.data?.invitee_email &&
        (res.data.flow_type === 'signup' || res.data.flow_type === 'register')
      ) {
        try {
          // callback 복귀 시 invite 정보를 참조할 수 있도록 미리 저장
          yield setEnvParam('inviteType', JSON.stringify(res.data))
          yield store.set('app.user.invite_token', token)

          const discoverResult = yield call(AuthService.discoverSaml, res.data.invitee_email)
          if (discoverResult.code === 200 && discoverResult.data?.sso_login_url) {
            const rawUrl = discoverResult.data.sso_login_url
            const ssoLoginUrl = rawUrl.replace('/api/v1/', '/')
            const { authURL } = conf.SERVER
            const separator = ssoLoginUrl.includes('?') ? '&' : '?'
            window.location.href = `${authURL}${ssoLoginUrl}${separator}login_hint=${encodeURIComponent(res.data.invitee_email)}`
            return
          }
        } catch (e) {
          console.error('SAML discover failed, falling back to default flow', e)
        }
      }

      // SAML이 아니거나 discover 실패 시 기존 플로우 진행
      if (store.get('app.user.token') != null) {
        yield put({ type: 'user/LOGOUT' })
      }

      if (res?.code === 200) {
        console.log(' --- basicInviteInfo res --- ', res)
        const { data } = res

        switch (data.flow_type) {
          case 'signup':
            yield setEnvParam('inviteType', JSON.stringify(data))
            yield store.set('app.user.invite_token', token)

            yield history.push('/auth/signup/register')
            return

          case 'register':
            yield setEnvParam('inviteType', JSON.stringify(data))
            yield store.set('app.user.invite_token', token)
            yield history.push(DEFAULT_AUTH_PAGE)

            return
          // case 'join':
          //   yield history.push(DEFAULT_AUTH_PAGE)
          //   return
          default:
            yield history.push(DEFAULT_AUTH_PAGE)
            return
        }
      } else {
        yield history.push(DEFAULT_AUTH_PAGE)
        return
      }
    }

    const response = yield call(
      isGlobal()
        ? mapAuthProviders[authProvider].currentBasicAccount
        : mapAuthProviders[authProvider].currentAccount,
    )
    if (response) {
      const {
        id,
        email,
        name,
        avatar,
        role,
        serverType,
        workspace,
        provisioning,
        authType,
        provider,
        workspaceList,
        paidLicense,
        recovery_option: recoveryOption,
        wrkp_initialized: wrkpInitialized,
        // 초대(register) ESCROW escrow 봉인용 워크스페이스 복구 공개키 — currentAccount/currentBasicAccount
        // 가 auth/info 에서 read(landing/account Fix C). 이 값을 redux 로 전달하지 않으면
        // RegisterMasterPW 가 escrow 봉인 재료를 못 받아 escrowed_recovery_key=null 로 전송됨.
        wrkp_public_key: wrkpPublicKey,
      } = response
      yield put({
        type: 'user/SET_STATE',
        payload: {
          id,
          name,
          email,
          avatar,
          role,
          authorized: true,
          serverType,
          workspace,
          provisioning,
          workspaceList,
          authType,
          provider,
          paidLicense,
          recovery_option: recoveryOption,
          wrkp_initialized: wrkpInitialized,
          wrkp_public_key: wrkpPublicKey,
        },
      })

      yield put({
        type: 'menu/RELOAD',
      })

      let curPath = window.location.pathname + window.location.search

      if (
        useByLogin &&
        (curPath.indexOf('/masterPW/register') === 0 ||
          curPath.indexOf('/masterPW/changePW') === 0 ||
          curPath.indexOf('/auth/signup/register') === 0)
      ) {
        const params = getSearchParams()
        const defaultPage = getConfig('defaultPage') || '/'
        curPath = params.path != null ? window.decodeURIComponent(params.path) : defaultPage
      }

      let success = false
      console.log(' --- authType : ', authType, ' resetPW : ', resetPW, ' curPath : ', curPath)
      if (resetPW === true) {
        if (curPath.indexOf('/masterPW/verify/changePW') === 0) {
          // 🔥 뒤로가기 시 masterPW/verify로 이동하게 수정
          const previousPage = document.referrer || '' // 이전 페이지 가져오기

          if (previousPage.includes('/masterPW/verify')) {
            yield history.push('/masterPW/verify')
          } else {
            yield history.push('/masterPW/verify/changePW')
          }
        } else if (curPath.indexOf('/masterPW/verify') === 0) {
          yield history.push('/masterPW/verify')
        } else {
          yield history.push(`/masterPW/verify/changePW?path=${curPath}`)
        }
      } else if (curPath.indexOf('/masterPW/verify') === 0) {
        yield history.push('/masterPW/verify')
      } else {
        switch (authType) {
          case 'login':
            if (getTempEnvParam('inviteRegisterLogin') === true) {
              yield history.push('/')
              yield removeTempEnvParam('inviteRegisterLogin')
            } else {
              const umk = yield call(getUMKFromLocal, true)
              const noLogin = yield select((state) => state.user.noLogin)
              if (umk != null && noLogin === false) {
                success = yield call(mapAuthProviders[authProvider].umkLogin, umk)
              }

              if (success === false) {
                const { path } = getSearchParams()

                const isAuthPage =
                  curPath.startsWith('/masterPW/login') || curPath.startsWith('/auth/')
                const isIpBlockedPage = curPath.startsWith('/ip-blocked')

                const pathToUse = isAuthPage || isIpBlockedPage ? path : path || curPath
                const redirectPath = pathToUse
                  ? `/masterPW/login?path=${encodeURIComponent(pathToUse)}`
                  : '/masterPW/login'

                yield history.push(redirectPath)
              }
            }

            break
          case 'signup':
          case 'register':
            yield redirectWithPathQuery(curPath, {
              targetPath: '/masterPW/register',
              defaultAddPath: true,
              skipPathPrefixes: ['/masterPW/register', '/auth/'],
            })
            break
          case 'change-password':
            yield redirectWithPathQuery(curPath, {
              targetPath: '/masterPW/changePW',
              defaultAddPath: false,
              skipPathPrefixes: ['/masterPW/changePW'],
              alwaysAddPathPrefixes: ['/masterPW', '/auth'],
            })
            break

          case 'expired': {
            const expiredData = getEnvParam('expiredData')
            if (expiredData != null) {
              yield redirectWithPathQuery(curPath, {
                targetPath: '/expired',
                alwaysSamePath: true,
              })
            }
            break
          }

          case 'common_auth_changePW':
            yield redirectWithPathQuery(curPath, {
              targetPath: '/auth/changePW',
              defaultAddPath: false,
              skipPathPrefixes: ['/auth/changePW'],
              alwaysAddPathPrefixes: ['/masterPW', '/auth'],
            })
            break

          case 'ip-blocked': {
            deleteUMKFromLocal()
            yield history.push('/ip-blocked')
            if (store.get('app.user.token') != null) {
              yield call(mapAuthProviders[authProvider].logout)
            }
            yield call(clearAttachmentCache)
            return
          }

          default:
        }
      }

      if (success === true) {
        // expiredData가 로컬 스토리지에 없으면 /expired 페이지로 이동하지 않음
        if (curPath.indexOf('/expired') === 0) {
          const expiredData = getEnvParam('expiredData')
          if (expiredData == null) {
            const defaultPage = getConfig('defaultPage') || '/'
            yield history.push(defaultPage)
            return
          }
        }
        yield history.push(curPath)
      }
    }
  } catch (error) {
    console.log(error)

    yield put({
      type: 'user/SET_STATE',
      payload: {
        authorized: false,
      },
    })
  }
}

export function* LOAD_CURRENT_ACCOUNT() {
  // viewer 엔드포인트는 계정 로딩 건너뛰기
  const { pathname } = window.location
  if (pathname.startsWith('/viewer')) {
    yield put({
      type: 'user/SET_STATE',
      payload: {
        loading: false,
      },
    })
    return
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })

  yield INNER_LOAD_CURRENT_ACCOUNT(false)

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })
}

export function* LOGOUT(action) {
  const { authProvider } = yield select((state) => state.settings)
  const fromExtension = action?.payload?.fromExtension === true
  const fromBroadcast = action?.payload?.fromBroadcast === true
  const shouldBroadcast = action?.payload?.broadcast === true

  // 다중 탭 동기 로그아웃: 사용자가 *직접 누른* 로그아웃만 다른 탭으로 전파한다
  // (broadcast 플래그 opt-in). 로그인/초대 흐름 내부에서 cleanup 용으로 dispatch
  // 되는 LOGOUT (invite accept, landing account, 팝업 차단 등) 은 이 플래그가 없어
  // 전파되지 않으므로, 한 탭의 로그인이 다른 탭을 auth/login 으로 튕기지 않는다.
  // BroadcastChannel 은 송신자 자신에게는 전달되지 않으므로 자가-로그아웃 루프 없음.
  if (shouldBroadcast) {
    try {
      const bc = new BroadcastChannel('user-logout')
      bc.postMessage({ type: 'user-logout' })
      bc.close()
    } catch (e) {
      // BroadcastChannel 미지원 환경은 무시
    }
  }

  // 익스텐션 / 다른 탭 broadcast 트리거: 서버 로그아웃 API 는 트리거한 탭이 이미
  // 호출했으므로 skipApi 로 로컬 cleanup 만 수행한다. UI 가 한 박자 늦지 않도록
  // state + 토큰을 동기로 정리한다. layouts 의 isUserAuthorized = authorized ||
  // token != null 가드라 둘 다 비워야 즉시 redirect. 나머지 app.user.* /
  // orgChartDB cleanup 은 아래 landing.logout 에서 이어서 수행.
  if (fromExtension || fromBroadcast) {
    store.remove('app.user.token')
    store.remove('app.user.refreshToken')
    yield put({
      type: 'user/SET_STATE',
      payload: {
        id: '',
        name: '',
        role: '',
        email: '',
        avatar: '',
        authorized: false,
        loading: false,
        resetPW: false,
        workspace: null,
        provisioning: false,
        recoveryUMK: false,
        error_shown: true,
        verificationEmail: '',
        workspaceList: [],
        leakedCount: null,
      },
    })
  }

  yield call(mapAuthProviders[authProvider].logout, {
    skipApi: fromExtension || fromBroadcast,
  })
  yield call(clearAttachmentCache)
  yield put({
    type: 'user/SET_STATE',
    payload: {
      id: '',
      name: '',
      role: '',
      email: '',
      avatar: '',
      authorized: false,
      loading: false,
      resetPW: false,
      workspace: null,
      provisioning: false,
      recoveryUMK: false,
      error_shown: true,
      verificationEmail: '',
      workspaceList: [],
      leakedCount: null,
      // PR-F: 로그아웃 시 종료코드 리셋 → 재로그인 후에도 여전히 만료면 restClient가 재감지(가드 해제)
      licenseTerminated: null,
    },
  })
}

export default function* rootSaga() {
  yield all([
    takeLeading(actions.LOGIN_BY_TOKEN, LOGIN_BY_TOKEN),
    takeLeading(actions.SET_RESET_PW, SET_RESET_PW),
    takeLatest(actions.LOAD_CURRENT_ACCOUNT, LOAD_CURRENT_ACCOUNT),
    takeLeading(actions.LOGOUT, LOGOUT),
    takeEvery(actions.UMK_LOGIN, UMK_LOGIN),
    takeLeading(actions.UMK_REGISTER, UMK_REGISTER),
    takeLeading(actions.UMK_CHANGEPW, UMK_CHANGEPW),
    takeLeading(actions.BASIC_REGISTER, BASIC_REGISTER),
    takeLeading(actions.BASIC_LOGIN, BASIC_LOGIN),
    takeLeading(actions.SELECT_WORKSPACE, SELECT_WORKSPACE),
    takeLeading(actions.SET_WORKSPACE_NAME, SET_WORKSPACE_NAME),
    takeLeading(actions.SET_USER_NAME, SET_USER_NAME),
    takeLeading(actions.SET_USER_AVATAR, SET_USER_AVATAR),
    takeLeading(actions.SET_LEAKED_RECORD_COUNT, SET_LEAKED_RECORD_COUNT),
    LOAD_CURRENT_ACCOUNT(), // run once on app load to check user auth
  ])
}
